import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withCtx as _withCtx, createElementVNode as _createElementVNode, withKeys as _withKeys, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "p-splitbutton toolbar-splitbutton"
}

import { computed, inject, ref, capitalize, nextTick, watch } from "vue";
import { BeakerSession, BeakerBaseCell } from 'beaker-kernel/src';
import { type BeakerNotebookComponentType } from './BeakerNotebook.vue';

import Button from "primevue/button";
import ChevronDownIcon from 'primevue/icons/chevrondown'
import { ButtonProps } from "primevue/button";
import SplitButton from 'primevue/splitbutton';
import InputGroup from "primevue/inputgroup";
import InputText from "primevue/inputtext";
import OverlayPanel from 'primevue/overlaypanel';
import Toolbar from "primevue/toolbar";

import OpenNotebookButton from "../dev-interface/OpenNotebookButton.vue";
import { downloadFileDOM, getDateTime } from '../../util';

export interface Props {
    defaultSeverity?: ButtonProps["badgeSeverity"];
    saveAvailable?: boolean;
    saveAsFilename?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BeakerNotebookToolbar',
  props: {
    defaultSeverity: { default: "info" },
    saveAvailable: { type: Boolean, default: false },
    saveAsFilename: {}
  },
  emits: [
    "notebook-saved",
    "open-file",
],
  setup(__props: any, { emit: __emit }) {

const session = inject<BeakerSession>('session');
const notebook = inject<BeakerNotebookComponentType>('notebook');
const cellMapping = inject<{[key: string]: {icon: string, modelClass: typeof BeakerBaseCell}}>('cell-component-mapping');

const addCellMenuItems = computed(() => {
    return Object.entries(cellMapping).map(([name, obj]) => {
        return {
            label: `${capitalize(name)} Cell`,
            icon: obj.icon,
            command: () => {
                const cell = new obj.modelClass({
                    source: "",
                });
                notebook.insertCellAfter(notebook.selectedCell(), cell, true);
            },
        }
    })
});

const emit = __emit

const props = __props;

const saveAsFilename = ref<string>(props.saveAsFilename);
const saveAsHoverMenuRef = ref();
const saveAsInputRef = ref();

watch(props, (oldValue, newValue) => {
    if (saveAsFilename.value !== newValue.saveAsFilename) {
        saveAsFilename.value = newValue.saveAsFilename;
    }
});

const resetSaveAsFilename = () => {
    saveAsFilename.value = `Beaker-Notebook_${getDateTime()}.ipynb`;
}

const resetNotebook = async () => {
    if (window.confirm(`This will reset your entire session, clearing the notebook and removing any updates to the environment. Proceed?`)) {
        session.reset();
        saveAsFilename.value = undefined;
        emit("notebook-saved", undefined);
        if (notebook.cellCount <= 0) {
            notebook.selectCell(session.addCodeCell("") as {id: string});
        }
    }
};

function loadNotebook(notebookJSON: any, filename: string) {
    emit("open-file", notebookJSON, filename);
}

async function saveNotebook() {
    const notebookContent = session.notebook.toIPynb();
    const contentsService = session.services.contents;
    if (!saveAsFilename.value) {
        resetSaveAsFilename();
    }
    const path = `./${saveAsFilename.value}`;
    const result = await contentsService.save(path, {
        type: "notebook",
        content: notebookContent,
        format: 'text',
    });
    emit("notebook-saved", result.path);
    saveAsFilename.value = result.path;
}

async function saveAs() {
    await saveNotebook();
    saveAsHoverMenuRef.value.hide();

}

function downloadNotebook() {
    const data = JSON.stringify(session.notebook.toIPynb(), null, 2);
    const filename = `Beaker-Notebook_${getDateTime()}.ipynb`;
    const mimeType = 'application/x-ipynb+json';
    downloadFileDOM(data, filename, mimeType);
}


return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_unref(Toolbar), { class: "notebook-toolbar" }, {
    start: _withCtx(() => [
      _renderSlot(_ctx.$slots, "start", {}, () => [
        _createVNode(_unref(SplitButton), {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(notebook).insertCellAfter())),
          class: "toolbar-splitbutton add-cell-button",
          icon: "pi pi-plus",
          size: "small",
          severity: props.defaultSeverity,
          model: addCellMenuItems.value,
          text: ""
        }, null, 8, ["severity", "model"]),
        _createVNode(_unref(Button), {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(notebook).removeCell())),
          icon: "pi pi-minus",
          size: "small",
          severity: props.defaultSeverity,
          text: ""
        }, null, 8, ["severity"]),
        _createVNode(_unref(Button), {
          onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(notebook).selectedCell().execute())),
          icon: "pi pi-play",
          size: "small",
          severity: props.defaultSeverity,
          text: ""
        }, null, 8, ["severity"]),
        _renderSlot(_ctx.$slots, "start-extra")
      ])
    ]),
    end: _withCtx(() => [
      _renderSlot(_ctx.$slots, "end", {}, () => [
        _withDirectives(_createVNode(_unref(Button), {
          onClick: resetNotebook,
          icon: "pi pi-refresh",
          size: "small",
          severity: props.defaultSeverity,
          text: ""
        }, null, 8, ["severity"]), [
          [
            _directive_tooltip,
            {value: 'Reset notebook', showDelay: 300},
            void 0,
            { bottom: true }
          ]
        ]),
        (props.saveAvailable)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _withDirectives(_createVNode(_unref(Button), {
                class: "p-splitbutton-defaultbutton",
                onClick: saveNotebook,
                icon: "pi pi-save",
                size: "small",
                severity: props.defaultSeverity,
                text: ""
              }, null, 8, ["severity"]), [
                [
                  _directive_tooltip,
                  {
                            value: 'Save locally as ' + (saveAsFilename.value ? saveAsFilename.value : 'a .ipynb file') ,
                            showDelay: 300
                        },
                  void 0,
                  { bottom: true }
                ]
              ]),
              _createVNode(_unref(Button), {
                class: "p-splitbutton-menubutton",
                size: "small",
                severity: props.defaultSeverity,
                text: "",
                onClick: _cache[3] || (_cache[3] = ($event: any) => {saveAsHoverMenuRef.value.toggle($event);})
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ChevronDownIcon))
                ]),
                _: 1
              }, 8, ["severity"]),
              _createVNode(_unref(OverlayPanel), {
                class: "saveas-overlay",
                ref_key: "saveAsHoverMenuRef",
                ref: saveAsHoverMenuRef,
                popup: true,
                onShow: _cache[7] || (_cache[7] = ($event: any) => {if (!saveAsFilename.value) {resetSaveAsFilename()};})
              }, {
                default: _withCtx(() => [
                  _cache[8] || (_cache[8] = _createElementVNode("div", null, "Save as:", -1)),
                  _createVNode(_unref(InputGroup), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(InputText), {
                        class: "saveas-input",
                        ref_key: "saveAsInputRef",
                        ref: saveAsInputRef,
                        modelValue: saveAsFilename.value,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((saveAsFilename).value = $event)),
                        onKeydown: _cache[5] || (_cache[5] = _withKeys(($event: any) => (saveAs()), ["enter"])),
                        autofocus: ""
                      }, null, 8, ["modelValue"]),
                      _createVNode(_unref(Button), {
                        label: "Save",
                        onClick: _cache[6] || (_cache[6] = ($event: any) => (saveAs()))
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 512)
            ]))
          : _createCommentVNode("", true),
        _withDirectives(_createVNode(_unref(Button), {
          onClick: downloadNotebook,
          icon: "pi pi-download",
          size: "small",
          severity: props.defaultSeverity,
          text: ""
        }, null, 8, ["severity"]), [
          [
            _directive_tooltip,
            {value: 'Download as .ipynb', showDelay: 300},
            void 0,
            { bottom: true }
          ]
        ]),
        _createVNode(OpenNotebookButton, {
          severity: _ctx.$props.defaultSeverity,
          onOpenFile: loadNotebook
        }, null, 8, ["severity"]),
        _renderSlot(_ctx.$slots, "end-extra")
      ])
    ]),
    _: 3
  }))
}
}

})